import React, { forwardRef } from 'react';
import { FieldError } from 'react-hook-form';

interface FormTextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: FieldError | string;
  helperText?: string;
  required?: boolean;
  showCharCount?: boolean;
  maxLength?: number;
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
}

const FormTextArea = forwardRef<HTMLTextAreaElement, FormTextAreaProps>(
  (
    {
      label,
      error,
      helperText,
      required,
      showCharCount,
      maxLength,
      resize = 'vertical',
      className = '',
      id,
      value = '',
      ...props
    },
    ref
  ) => {
    const inputId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;
    const errorMessage = typeof error === 'string' ? error : error?.message;
    const hasError = !!errorMessage;

    const currentLength = typeof value === 'string' ? value.length : 0;
    const showCounter = showCharCount || maxLength;

    const resizeClass = {
      none: 'resize-none',
      vertical: 'resize-y',
      horizontal: 'resize-x',
      both: 'resize',
    }[resize];

    return (
      <div className="space-y-1">
        {/* Label */}
        {label && (
          <div className="flex items-center justify-between">
            <label htmlFor={inputId} className="block text-meta font-medium text-gray-700">
              {label}
              {required && <span className="text-red-500 ml-1">*</span>}
            </label>

            {/* Character Count */}
            {showCounter && (
              <span className="text-meta text-gray-500">
                {currentLength}
                {maxLength && `/${maxLength}`}
              </span>
            )}
          </div>
        )}

        {/* TextArea */}
        <textarea
          ref={ref}
          id={inputId}
          value={value}
          maxLength={maxLength}
          className={`
            block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm
            text-body-sm text-gray-900 placeholder-gray-500
            focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400
            disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
            ${hasError ? 'border-red-300 focus:ring-red-400 focus:border-red-400' : ''}
            ${resizeClass}
            ${className}
          `}
          rows={4}
          aria-invalid={hasError}
          aria-describedby={
            errorMessage ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
          }
          {...props}
        />

        {/* Helper Text */}
        {helperText && !hasError && (
          <p id={`${inputId}-helper`} className="text-meta text-gray-500">
            {helperText}
          </p>
        )}

        {/* Error Message */}
        {hasError && (
          <p id={`${inputId}-error`} className="text-meta text-red-600" role="alert">
            {errorMessage}
          </p>
        )}
      </div>
    );
  }
);

FormTextArea.displayName = 'FormTextArea';

export default FormTextArea;
